import React, { useMemo, useState, useCallback } from'react';
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight } from'lucide-react';
import clsx from'clsx';
import ExportButton from'../ExportButton';

/**
 * Modern read-only data table built on a plain HTML <table> + Tailwind.
 *
 * Drop-in replacement for the old AG Grid based DataTable. Call sites pass
 * the same AG-Grid-style column descriptor (`headerName`,`field`,`flex`,
 *`width`/`minWidth`,`type:'numericColumn'`,`valueFormatter`,
 *`cellRenderer`,`cellStyle`) so no page changes are required.
 *
 * Why not AG Grid: for our use case (small to medium read-only summary
 * tables) AG Grid's chrome was fighting the design system — header/cell
 * alignment kept drifting, dark mode required CSS variable plumbing, and
 * the table never quite felt like the rest of the app. This component
 * gives perfect alignment, tabular numerals, sticky headers, sortable
 * columns, lightweight pagination, dark-mode parity, and CSV/JSON export.
 */
export default function DataTable({
 data,
 columns,
 title,
 subtitle,
 height = 420,
 pageSize: initialPageSize = 25,
 exportFilename,
 onRowClick,
 rowClassRules,
 initialSort = null,
 emptyMessage ='No data available.',
}) {
 const rows = Array.isArray(data) ? data : [];

 // ── Sorting ─────────────────────────────────────────────────────────
 // `initialSort` ({ key, dir }) lets a caller land the table pre-sorted —
 // used when navigating in from a KPI card (e.g. "Error Rate" deep-links
 // to this table already ranked by error rate) so the relevant rows are
 // at the top without the user having to click a header.
 const [sort, setSort] = useState(initialSort); // { key, dir }
 const sorted = useMemo(() => {
 if (!sort) return rows;
 const col = columns.find((c) => (c.field || c.colId) === sort.key);
 if (!col) return rows;
 const isNumeric = col.type ==='numericColumn';
 const copy = [...rows];
 copy.sort((a, b) => {
 const av = a?.[sort.key];
 const bv = b?.[sort.key];
 let cmp;
 if (isNumeric) {
 cmp = (Number(av) || 0) - (Number(bv) || 0);
 } else if (av == null && bv == null) {
 cmp = 0;
 } else if (av == null) {
 cmp = -1;
 } else if (bv == null) {
 cmp = 1;
 } else {
 cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
 }
 return sort.dir ==='asc' ? cmp : -cmp;
 });
 return copy;
 }, [rows, sort, columns]);

 const toggleSort = useCallback((col) => {
 if (col.sortable === false) return;
 const key = col.field || col.colId;
 if (!key) return;
 setSort((prev) => {
 if (!prev || prev.key !== key) {
 return { key, dir: col.type ==='numericColumn' ?'desc' :'asc' };
 }
 return { key, dir: prev.dir ==='asc' ?'desc' :'asc' };
 });
 }, []);

 // ── Pagination ──────────────────────────────────────────────────────
 const [pageSize, setPageSize] = useState(initialPageSize);
 const [page, setPage] = useState(0);
 const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
 const safePage = Math.min(page, pageCount - 1);
 const pageRows = useMemo(
 () => sorted.slice(safePage * pageSize, safePage * pageSize + pageSize),
 [sorted, safePage, pageSize]
 );

 // Reset to first page when data shrinks below current page.
 React.useEffect(() => {
 if (page > pageCount - 1) setPage(0);
 }, [page, pageCount]);

 // ── Column width style ──────────────────────────────────────────────
 // Mirror AG Grid's`flex`/`width`/`minWidth` to CSS.`flex` becomes a
 // table-column-level flex via colgroup widths is awkward, so instead we
 // use min-width on <th> and let`width: <flex>%` distribute the rest.
 const totalFlex = columns.reduce((sum, c) => sum + (c.width ? 0 : (c.flex || 1)), 0);
 const colStyle = (col) => {
 const style = {};
 if (col.width) {
 style.width =`${col.width}px`;
 style.minWidth =`${col.width}px`;
 } else {
 const pct = ((col.flex || 1) / totalFlex) * 100;
 style.width =`${pct}%`;
 if (col.minWidth) style.minWidth =`${col.minWidth}px`;
 }
 return style;
 };

 // ── Row class rules (AG Grid compat) ────────────────────────────────
 const computeRowClass = useCallback(
 (row) => {
 if (!rowClassRules) return'';
 const classes = [];
 for (const [cls, predicate] of Object.entries(rowClassRules)) {
 try {
 if (typeof predicate ==='function' && predicate({ data: row })) {
 classes.push(cls);
 }
 } catch {
 /* ignore predicate errors */
 }
 }
 return classes.join('');
 },
 [rowClassRules]
 );

 // ── Cell rendering with AG-Grid-shaped params ───────────────────────
 const renderCell = (col, row) => {
 const key = col.field || col.colId;
 const value = key ? row?.[key] : undefined;
 const params = { value, data: row, node: { data: row } };

 let content;
 if (typeof col.cellRenderer ==='function') {
 content = col.cellRenderer(params);
 } else if (typeof col.valueFormatter ==='function') {
 content = col.valueFormatter(params);
 } else {
 content = value == null || value ==='' ?'—' : String(value);
 }

 let style;
 if (typeof col.cellStyle ==='function') {
 try {
 style = col.cellStyle(params) || undefined;
 } catch {
 style = undefined;
 }
 } else if (col.cellStyle && typeof col.cellStyle ==='object') {
 style = col.cellStyle;
 }

 return { content, style };
 };

 // ── Header & layout ─────────────────────────────────────────────────
 return (
 <div className="chart-container">
 {(title || exportFilename || rows.length > 0) && (
 <div className="flex items-start justify-between gap-3 mb-3">
 <div className="min-w-0">
 {title && (
 <h3 className="text-sm font-semibold text-gray-700 truncate">
 {title}
 </h3>
 )}
 {subtitle && (
 <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
 )}
 </div>
 <div className="flex items-center gap-3 flex-shrink-0">
 <span className="text-xs text-gray-400 tabular-nums">
 {sorted.length.toLocaleString()} {sorted.length === 1 ?'row' :'rows'}
 </span>
 {exportFilename && <ExportButton data={sorted} filename={exportFilename} />}
 </div>
 </div>
 )}

 <div
 className="overflow-auto rounded-lg border border-gray-200"
 style={{ maxHeight: height }}
 >
 <table className="min-w-full text-sm" style={{ tableLayout:'fixed', minWidth:`${Math.max(columns.length * 120, 640)}px` }}>
 <thead className="bg-gray-50 sticky top-0 z-10 backdrop-blur">
 <tr>
 {columns.map((col) => {
 const key = col.field || col.colId;
 const active = sort?.key === key;
 const sortable = col.sortable !== false && !!key;
 const isRight = col.type ==='numericColumn';
 const Icon = !active ? ChevronsUpDown : sort.dir ==='asc' ? ArrowUp : ArrowDown;
 return (
 <th
 key={key || col.headerName}
 scope="col"
 style={colStyle(col)}
 className={clsx(
'px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider',
'text-gray-500 select-none',
'border-b border-gray-200',
 isRight ?'text-right' :'text-left'
 )}
 aria-sort={
 active ? (sort.dir ==='asc' ?'ascending' :'descending') :'none'
 }
 >
 {sortable ? (
 <button
 type="button"
 onClick={() => toggleSort(col)}
 className={clsx(
'inline-flex items-center gap-1 transition',
'hover:text-gray-900',
 isRight &&'flex-row-reverse',
 active &&'text-gray-900'
 )}
 >
 <span>{col.headerName}</span>
 <Icon className={clsx('w-3 h-3', active ?'opacity-100' :'opacity-40')} />
 </button>
 ) : (
 <span>{col.headerName}</span>
 )}
 </th>
 );
 })}
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {pageRows.length === 0 ? (
 <tr>
 <td
 colSpan={columns.length}
 className="px-4 py-12 text-center text-sm text-gray-400"
 >
 {emptyMessage}
 </td>
 </tr>
 ) : (
 pageRows.map((row, idx) => {
 const rowCls = computeRowClass(row);
 return (
 <tr
 key={row?.id ?? row?.OperationId ?? row?.SubscriptionId ?? idx}
 onClick={onRowClick ? () => onRowClick(row) : undefined}
 className={clsx(
'transition-colors',
'hover:bg-gray-50',
 onRowClick &&'cursor-pointer',
 rowCls
 )}
 >
 {columns.map((col) => {
 const isRight = col.type ==='numericColumn';
 const key = col.field || col.colId;
 const { content, style } = renderCell(col, row);
 return (
 <td
 key={key || col.headerName}
 style={style}
 className={clsx(
'px-4 py-2.5 whitespace-nowrap overflow-hidden text-ellipsis',
 isRight
 ?'text-right tabular-nums text-gray-700'
 :'text-left text-gray-700'
 )}
 >
 {content}
 </td>
 );
 })}
 </tr>
 );
 })
 )}
 </tbody>
 </table>
 </div>

 {sorted.length > pageSize && (
 <div className="flex items-center justify-between gap-3 mt-3 px-1 text-xs text-gray-500">
 <div className="flex items-center gap-2">
 <span>Rows per page</span>
 <select
 value={pageSize}
 onChange={(e) => {
 setPageSize(Number(e.target.value));
 setPage(0);
 }}
 className="bg-transparent border border-gray-300 rounded-md px-1.5 py-0.5
 text-xs hover:bg-gray-50 focus:outline-none
 focus:ring-1 focus:ring-brand-500"
 >
 {[10, 25, 50, 100].map((n) => (
 <option key={n} value={n}>
 {n}
 </option>
 ))}
 </select>
 </div>
 <div className="flex items-center gap-3">
 <span className="tabular-nums">
 {safePage * pageSize + 1}
 {'–'}
 {Math.min((safePage + 1) * pageSize, sorted.length)} of{''}
 {sorted.length.toLocaleString()}
 </span>
 <div className="flex items-center gap-1">
 <button
 type="button"
 onClick={() => setPage((p) => Math.max(0, p - 1))}
 disabled={safePage === 0}
 className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
 aria-label="Previous page"
 >
 <ChevronLeft className="w-4 h-4" />
 </button>
 <button
 type="button"
 onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
 disabled={safePage >= pageCount - 1}
 className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
 aria-label="Next page"
 >
 <ChevronRight className="w-4 h-4" />
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
